import type { OwnershipInviteAcceptance, OwnershipTransfer, UserSearchResult } from '@/features/ownership/types'
import { db } from '@/lib/firebase'
import { CompanyUserRole } from '@/types/companyUser'
import { UserType } from '@/types/user'
import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore'
import { updateDoc as updateUserDoc, doc as userDoc } from 'firebase/firestore'
import { v4 as uuidv4 } from 'uuid'
import { createCompanyUser, getCompanyUser, removeCompanyUser, updateCompany } from './companies'
import { sendOwnershipInviteEmail } from './emailService'

export const createOwnershipTransfer = async (
  companyId: string,
  toEmail: string,
  fromUserId: string,
  fromUserName?: string,
  companyName?: string,
): Promise<void> => {
  if (!companyId || !toEmail || !fromUserId) {
    throw new Error('Company ID, email, and from user ID are required')
  }

  // Validate that the current user is the owner of the company
  const companyDoc = await getDoc(doc(db, 'companies', companyId))
  if (!companyDoc.exists()) {
    throw new Error('Company not found')
  }

  const company = companyDoc.data()
  const currentOwnerID = company.currentOwnerID || company.createdByUserID

  if (currentOwnerID !== fromUserId) {
    throw new Error('Only the current owner can transfer ownership')
  }

  // Check if the target user already owns a company (unless they're super admin)
  const targetUserQuery = query(collection(db, 'users'), where('email', '==', toEmail.toLowerCase()), limit(1))

  const targetUserSnapshot = await getDocs(targetUserQuery)
  if (!targetUserSnapshot.empty) {
    const targetUser = targetUserSnapshot.docs[0].data()

    // If not super admin, check if they already own a company
    if (targetUser.userType !== 'SUPER_ADMIN') {
      const ownedCompaniesQuery = query(
        collection(db, 'companies'),
        where('currentOwnerID', '==', targetUserSnapshot.docs[0].id),
        limit(1),
      )

      const ownedCompaniesSnapshot = await getDocs(ownedCompaniesQuery)
      if (!ownedCompaniesSnapshot.empty) {
        throw new Error('Target user already owns a company and cannot receive ownership transfer')
      }
    }
  }

  const transferData: Omit<OwnershipTransfer, 'id'> = {
    companyId,
    fromUserId,
    toEmail: toEmail.toLowerCase(),
    token: uuidv4(),
    status: 'pending',
    createdAt: Timestamp.now(),
    expiresAt: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)), // 7 days
    fromUserName,
    companyName,
  }

  const transferDoc = await addDoc(collection(db, 'ownershipTransfers'), transferData)

  // Send ownership invitation email
  try {
    await sendOwnershipInviteEmail({
      transferId: transferDoc.id,
      companyName: companyName || 'Unknown Company',
      fromUserName: fromUserName || 'Unknown User',
      toEmail: toEmail.toLowerCase(),
      token: transferData.token,
    })
  } catch (error: any) {
    // Don't throw error - transfer is created, email failure shouldn't block the process
  }
}

export const getOwnershipTransfer = async (id: string): Promise<OwnershipTransfer> => {
  if (!id?.trim()) {
    throw new Error('Transfer ID is required')
  }

  const docRef = doc(db, 'ownershipTransfers', id)
  const docSnap = await getDoc(docRef)

  if (!docSnap.exists()) {
    throw new Error('Ownership transfer not found')
  }

  const data = docSnap.data()
  return {
    id: docSnap.id,
    ...data,
    expiresAt: data.expiresAt?.toDate() || new Date(),
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
  } as OwnershipTransfer
}

export const getOwnershipTransferByToken = async (token: string): Promise<OwnershipTransfer> => {
  if (!token?.trim()) {
    throw new Error('Token is required')
  }

  const q = query(
    collection(db, 'ownershipTransfers'),
    where('token', '==', token),
    where('status', 'in', ['pending', 'pending_user_creation']),
    limit(1),
  )

  const querySnapshot = await getDocs(q)

  if (querySnapshot.empty) {
    throw new Error('Invalid or expired transfer token')
  }

  const doc = querySnapshot.docs[0]
  const data = doc.data()

  // Check if expired
  const expiresAt = data.expiresAt?.toDate()
  if (expiresAt && expiresAt < new Date()) {
    throw new Error('Transfer token has expired')
  }

  return {
    id: doc.id,
    ...data,
    expiresAt: expiresAt || new Date(),
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
  } as OwnershipTransfer
}

export const searchUsersByEmail = async (emailQuery: string): Promise<UserSearchResult[]> => {
  if (!emailQuery?.trim() || emailQuery.length < 2) {
    return []
  }

  const q = query(
    collection(db, 'users'),
    where('email', '>=', emailQuery.toLowerCase()),
    where('email', '<=', `${emailQuery.toLowerCase()}\uf8ff`),
    orderBy('email'),
    limit(10),
  )

  const querySnapshot = await getDocs(q)

  return querySnapshot.docs.map((doc) => {
    const data = doc.data()
    return {
      id: doc.id,
      email: data.email || '',
      fullName: data.fullName || '',
      userType: data.userType || 'pr_manager',
      picUrl: data.picUrl,
    }
  })
}

export const acceptOwnershipTransferWithUserCreation = async (
  acceptance: OwnershipInviteAcceptance & { password: string; fullName: string },
): Promise<void> => {
  if (!acceptance.token?.trim()) {
    throw new Error('Token is required')
  }

  if (!acceptance.password?.trim()) {
    throw new Error('Password is required')
  }

  if (!acceptance.fullName?.trim()) {
    throw new Error('Full name is required')
  }

  // Get the transfer details
  const transfer = await getOwnershipTransferByToken(acceptance.token)

  // Check if user already exists
  const existingUserQuery = query(
    collection(db, 'users'),
    where('email', '==', transfer.toEmail.toLowerCase()),
    limit(1),
  )
  const existingUserSnapshot = await getDocs(existingUserQuery)

  if (!existingUserSnapshot.empty) {
    throw new Error('User with this email already exists')
  }

  // Create Firebase Auth user
  let userId: string
  try {
    const { createUserWithEmailAndPassword, getAuth } = await import('firebase/auth')
    const auth = getAuth()

    const userCredential = await createUserWithEmailAndPassword(auth, transfer.toEmail, acceptance.password)
    userId = userCredential.user.uid
  } catch (authError: any) {
    if (authError.code === 'auth/email-already-in-use') {
      throw new Error('An account with this email already exists')
    }
    throw new Error(`Failed to create account: ${authError.message}`)
  }

  // Create user document in Firestore
  // This will trigger the onAuthUserCreate Cloud Function which will:
  // 1. Detect the pending ownership transfer by email
  // 2. Complete the ownership transfer (update company, create companyUser, etc.)
  // 3. Update user type to ADMIN
  // 4. Mark emailVerified as true
  const userDocRef = doc(db, 'users', userId)
  await setDoc(userDocRef, {
    id: userId,
    email: transfer.toEmail.toLowerCase(),
    fullName: acceptance.fullName,
    userType: UserType.PR_MANAGER, // Will be updated to ADMIN by Cloud Function
    createdAt: Timestamp.now(),
  })

  // Wait a moment for the Cloud Function to complete
  // This is a simple approach - in production, you might want to poll the transfer status
  await new Promise((resolve) => setTimeout(resolve, 2000))
}

export const acceptOwnershipTransfer = async (
  acceptance: OwnershipInviteAcceptance & { userId: string },
): Promise<void> => {
  if (!acceptance.token?.trim()) {
    throw new Error('Token is required')
  }

  if (!acceptance.userId?.trim()) {
    throw new Error('User ID is required')
  }

  // Get the transfer details
  const transfer = await getOwnershipTransferByToken(acceptance.token)

  // Verify the user exists and has the correct email
  const userDocRef = doc(db, 'users', acceptance.userId)
  const userDocSnap = await getDoc(userDocRef)
  if (!userDocSnap.exists()) {
    throw new Error('User not found')
  }

  const user = userDocSnap.data()
  if (user.email?.toLowerCase() !== transfer.toEmail.toLowerCase()) {
    throw new Error('User email does not match transfer email')
  }

  // Get the company to update
  const companyDoc = await getDoc(doc(db, 'companies', transfer.companyId))
  if (!companyDoc.exists()) {
    throw new Error('Company not found')
  }

  const company = companyDoc.data()
  const currentOwnerId = company.currentOwnerID || company.createdByUserID

  // Update user type to ADMIN
  await updateUserDoc(userDoc(db, 'users', acceptance.userId), {
    userType: UserType.ADMIN,
    updatedAt: Timestamp.now(),
  })

  // Update company ownership
  await updateDoc(doc(db, 'companies', transfer.companyId), {
    currentOwnerID: acceptance.userId,
    updatedAt: Timestamp.now(),
  })

  // Remove old owner from companiesUsers if they exist and are different from new owner
  if (currentOwnerId && currentOwnerId !== acceptance.userId) {
    try {
      const existingCompanyUser = await getCompanyUser(transfer.companyId, currentOwnerId)
      if (existingCompanyUser) {
        try {
          await removeCompanyUser(transfer.companyId, currentOwnerId)
        } catch (error) {
          // Try direct deletion as fallback
          const directId = `${transfer.companyId}_${currentOwnerId}`
          try {
            await deleteDoc(doc(db, 'companiesUsers', directId))
          } catch (directError) {
            // Log error but don't fail the transfer
          }
        }
      }
    } catch (error) {
      // Log error but don't fail the transfer
    }
  }

  // Add new owner to companiesUsers
  try {
    await createCompanyUser({
      companyID: transfer.companyId,
      userID: acceptance.userId,
      role: CompanyUserRole.ADMIN,
    })
  } catch (error) {
    // Log error but don't fail the transfer
  }

  // Update the transfer status
  await updateDoc(doc(db, 'ownershipTransfers', transfer.id), {
    status: 'completed',
    completedAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  })
}

export const cancelOwnershipTransfer = async (transferId: string): Promise<void> => {
  await updateDoc(doc(db, 'ownershipTransfers', transferId), {
    status: 'cancelled',
    updatedAt: Timestamp.now(),
  })
}

export const completePendingOwnershipTransfer = async (userEmail: string, userId: string): Promise<boolean> => {
  try {
    // Look for pending ownership transfers for this email
    const q = query(
      collection(db, 'ownershipTransfers'),
      where('toEmail', '==', userEmail.toLowerCase()),
      where('status', 'in', ['pending', 'pending_user_creation']),
      limit(1),
    )

    const querySnapshot = await getDocs(q)
    if (querySnapshot.empty) {
      return false // No pending transfer found
    }

    const transferDoc = querySnapshot.docs[0]
    const transfer = { id: transferDoc.id, ...transferDoc.data() } as OwnershipTransfer

    // Check if transfer is expired
    const expiresAt = transfer.expiresAt instanceof Timestamp ? transfer.expiresAt.toDate() : transfer.expiresAt
    if (expiresAt && expiresAt < new Date()) {
      return false // Transfer expired
    }

    // Get the company to update
    const companyDoc = await getDoc(doc(db, 'companies', transfer.companyId))
    if (!companyDoc.exists()) {
      return false // Company not found
    }

    const company = companyDoc.data()
    const currentOwnerId = company.currentOwnerID || company.createdByUserID

    // Update user type to ADMIN
    await updateUserDoc(userDoc(db, 'users', userId), {
      userType: UserType.ADMIN,
      updatedAt: Timestamp.now(),
    })

    // Update company ownership
    await updateCompany(transfer.companyId, {
      currentOwnerID: userId,
    })

    // Remove old owner from companiesUsers if they exist and are different from new owner
    if (currentOwnerId && currentOwnerId !== userId) {
      try {
        const existingCompanyUser = await getCompanyUser(transfer.companyId, currentOwnerId)
        if (existingCompanyUser) {
          try {
            await removeCompanyUser(transfer.companyId, currentOwnerId)
          } catch (error) {
            // Try direct deletion as fallback
            const directId = `${transfer.companyId}_${currentOwnerId}`
            try {
              await deleteDoc(doc(db, 'companiesUsers', directId))
            } catch (directError) {
              // Log error but don't fail the transfer
            }
          }
        }
      } catch (error) {
        // Log error but don't fail the transfer
      }
    }

    // Add new owner to companiesUsers
    try {
      await createCompanyUser({
        companyID: transfer.companyId,
        userID: userId,
        role: CompanyUserRole.ADMIN,
      })
    } catch (error) {
      // Log error but don't fail the transfer
    }

    // Update the transfer status
    await updateDoc(doc(db, 'ownershipTransfers', transfer.id), {
      status: 'completed',
      completedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    })

    return true
  } catch (error) {
    return false
  }
}

export const getCompanyOwnershipTransfers = async (companyId: string): Promise<OwnershipTransfer[]> => {
  try {
    const q = query(
      collection(db, 'ownershipTransfers'),
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc'),
    )

    const querySnapshot = await getDocs(q)

    return querySnapshot.docs.map((doc) => {
      const data = doc.data()
      const transfer = {
        ...data,
        id: doc.id,
        expiresAt: data.expiresAt?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        completedAt: data.completedAt?.toDate(),
      }
      return transfer as unknown as OwnershipTransfer
    })
  } catch (error) {
    throw new Error('Failed to fetch ownership transfers')
  }
}
